import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";

export const THEME_KEY = "fxurl.theme";
export const normalizeTheme = (value) => ["light", "dark", "auto"].includes(value) ? value : "auto";
const ThemeContext = createContext({ mode: "auto", resolvedTheme: "light", setMode: () => {} });
export const useTheme = () => useContext(ThemeContext);

export function ThemeProvider({ children }) {
  const [mode, updateMode] = useState(() => {
    try { return normalizeTheme(localStorage.getItem(THEME_KEY)); }
    catch { return "auto"; }
  });
  const [systemDark, setSystemDark] = useState(() => window.matchMedia?.("(prefers-color-scheme: dark)").matches || false);
  const resolvedTheme = mode === "auto" ? (systemDark ? "dark" : "light") : mode;
  const setMode = (value) => {
    const next = normalizeTheme(value);
    updateMode(next);
    try { localStorage.setItem(THEME_KEY, next); } catch {}
  };

  useLayoutEffect(() => {
    document.documentElement.dataset.bsTheme = resolvedTheme;
    document.documentElement.style.colorScheme = resolvedTheme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", resolvedTheme === "dark" ? "#181a1e" : "#ffffff");
  }, [resolvedTheme]);

  useEffect(() => {
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!media) return;
    const onChange = () => setSystemDark(media.matches);
    onChange();
    if (media.addEventListener) media.addEventListener("change", onChange);
    else media.addListener(onChange);
    return () => {
      if (media.removeEventListener) media.removeEventListener("change", onChange);
      else media.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    const onStorage = (event) => {
      if ((event.key === THEME_KEY || event.key === null) && (!event.storageArea || event.storageArea === localStorage)) {
        updateMode(normalizeTheme(event.newValue));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key.toLowerCase() !== "d" || event.ctrlKey || event.metaKey || event.altKey || event.repeat || event.isComposing || event.defaultPrevented) return;
      const target = event.target;
      if (target instanceof Element && (target.closest('input, textarea, select, [role="textbox"], [role="combobox"]') || target.closest('[contenteditable]:not([contenteditable="false"])'))) return;
      event.preventDefault();
      setMode(resolvedTheme === "dark" ? "light" : "dark");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [resolvedTheme]);

  return <ThemeContext.Provider value={{ mode, resolvedTheme, setMode }}>{children}</ThemeContext.Provider>;
}